import { Injectable } from "@angular/core";
import { LocalStorageService } from "./local-storage.service";
import {
  IRequestPwdRecovery,
  IResetPassword,
  IUserCredentials,
} from "../data-types/user.interface";
import { HttpClientService } from "./http-client.service";
import { EStorageType } from "../data-types/storage.enum";
import { ICoworking } from "../data-types/coworking.interface";
import { IBankInfo } from "../data-types/bank-info.interface";
import { IPriceType, ISpace, ISpaceType } from "../data-types/space.interface";
import { collapseTextChangeRangesAcrossMultipleVersions } from "typescript";
import { ICompany } from "../data-types/company.interface";
import { Subject } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class CompanyService {
  static instance: CompanyService = new CompanyService();
  public newCompanySelected = new Subject();
  public companyModified = new Subject();
  public newCompanyAdded = new Subject();
  constructor() {
    return (CompanyService.instance = CompanyService.instance || this);
  }

  public async createCompany(company: ICompany): Promise<any> {
    return new Promise((resolve, reject) => {
      let url: string = "companies";

      let headers: any = {
        Authorization: `Bearer ${LocalStorageService.instance.get(
          EStorageType.Token
        )}`,
      };
      let body: ICompany = company;

      HttpClientService.instance
        .post(url, headers, body)
        .then((result: any) => {
          resolve(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async createFirstCompany(company: ICompany): Promise<any> {
    return new Promise((resolve, reject) => {
      let url: string = "companies/first";
      let headers: any = {};
      let body: ICompany = company;
      HttpClientService.instance
        .post(url, headers, body)
        .then((result: any) => {
          resolve(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async uploadMediaToCompany(
    mediasFormData: FormData,
    companyId
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      let url: string = "companies/" + companyId + "/upload-file";
      let headers: any = {
        "content-type": "multipart/form-data",
      };
      let body = mediasFormData;

      HttpClientService.instance
        .post(url, headers, body)
        .then((result: any) => {
          resolve(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async putMediaToCompany(
    mediasFormData: FormData,
    companyId
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      let url: string = "companies/" + companyId + "/upload-file";
      let headers: any = {
        "content-type": "multipart/form-data",
        Authorization: `Bearer ${LocalStorageService.instance.get(
          EStorageType.Token
        )}`,
      };
      let body = mediasFormData;

      HttpClientService.instance
        .put(url, headers, body)
        .then((result: any) => {
          resolve(result);
          this.companyModified.next(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async createEmployeesToCompany(
    employees: Array<any>,
    companyId
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      let url: string = "companies/" + companyId + "/coworkers";
      let headers: any = {
        "content-type": "application/json",
      };
      let body = { coworkers: employees };

      HttpClientService.instance
        .post(url, headers, body)
        .then((result: any) => {
          console.log(result);
          resolve(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async putEmployeesToCompany(
    employees: Array<any>,
    companyId
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      let url: string = "companies/" + companyId + "/coworkers";

      let headers: any = {
        "content-type": "application/json",
        Authorization: `Bearer ${LocalStorageService.instance.get(
          EStorageType.Token
        )}`,
      };
      let body = { coworkers: employees };

      HttpClientService.instance
        .put(url, headers, body)
        .then((result: any) => {
          console.log(result);
          resolve(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async postEmployeesToCompany(
    employees: Array<any>,
    companyId
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      let url: string = "companies/" + companyId + "/coworkers";

      let headers: any = {
        "content-type": "application/json",
        Authorization: `Bearer ${LocalStorageService.instance.get(
          EStorageType.Token
        )}`,
      };
      let body = { coworkers: employees };

      HttpClientService.instance
        .post(url, headers, body)
        .then((result: any) => {
          console.log(result);
          resolve(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async addBankInfo(
    encryptedData: string,
    companyId: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      let url: string = "companies/" + companyId + "/payment-info";
      let headers: any = {};
      let body: any = { data: encryptedData };

      HttpClientService.instance
        .post(url, headers, body)
        .then((result: any) => {
          resolve(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async getCompanyInfo(companyId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      let url: string = "companies/" + companyId;
      let headers: any = {
        Authorization: `Bearer ${LocalStorageService.instance.get(
          EStorageType.Token
        )}`,
      };

      HttpClientService.instance
        .get(url, headers)
        .then((result: any) => {
          resolve(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async putCompanyInfo(
    companyId: string,
    company: ICompany
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      let url: string = "companies/" + companyId;
      let headers: any = {
        Authorization: `Bearer ${LocalStorageService.instance.get(
          EStorageType.Token
        )}`,
      };
      let body: ICompany = company;
      HttpClientService.instance
        .put(url, headers, body)
        .then((result: any) => {
          resolve(result);
          console.log(result);
          this.companyModified.next(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async getCompanyPaymentInfo(companyId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      let url: string = "companies/" + companyId + "/payment-info";
      let headers: any = {
        Authorization: `Bearer ${LocalStorageService.instance.get(
          EStorageType.Token
        )}`,
      };
      HttpClientService.instance
        .get(url, headers)
        .then((result: any) => {
          resolve(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async getCompanyEmployees(companyId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      let url: string = "companies/" + companyId + "/employees";
      let headers: any = {
        Authorization: `Bearer ${LocalStorageService.instance.get(
          EStorageType.Token
        )}`,
      };
      HttpClientService.instance
        .get(url, headers)
        .then((result: any) => {
          resolve(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async getCompanyStates(): Promise<any> {
    return new Promise((resolve, reject) => {
      let url: string = "companies/states";
      let headers: any = {
        Authorization: `Bearer ${LocalStorageService.instance.get(
          EStorageType.Token
        )}`,
      };

      HttpClientService.instance
        .get(url, headers)
        .then((result: any) => {
          resolve(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async getCompanySpendings(companyId: number, params, pageSize): Promise<any> {
    console.log(params);
    const newParams = {
      page_size: pageSize
    };
    if (params.page) {
      newParams['page'] = params.page;
    }

    if (params.dateStart) {
      newParams['dateStart'] = params.dateStart;
    }

    if (params.dateEnd) {
      newParams['dateEnd'] = params.dateEnd;
    }

    console.log('new params!!!', newParams);
    const queryParams = Object.keys(newParams)
      .filter((key) => newParams[key] !== null)
      .map((key) => key + '=' + newParams[key])
      .join('&');
    console.log(queryParams);
    return new Promise((resolve, reject) => {
      const url: string =
        'companies/' + companyId + '/spendings?' + queryParams;
      const headers: any = {
        Authorization: `Bearer ${LocalStorageService.instance.get(
          EStorageType.Token
        )}`,
      };

      HttpClientService.instance
        .get(url, headers)
        .then((result: any) => {
          resolve(result);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }
}
